/** Characters markdown adds or drops when rendered, ignored on both sides when finding a quote. */
const IGNORED = /[*_`|#>\\]/

const EQUIVALENT: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '“': '"',
  '”': '"',
  '–': '-',
  '—': '-',
  ' ': ' ',
}

function normalizeChar(char: string): string | null {
  if (IGNORED.test(char)) return null
  const mapped = EQUIVALENT[char] ?? char
  return /\s/.test(mapped) ? ' ' : mapped.toLowerCase()
}

export function normalizeText(text: string): string {
  let out = ''
  for (const char of text) {
    const value = normalizeChar(char)
    if (value === null || (value === ' ' && (out === '' || out.endsWith(' ')))) continue
    out += value
  }
  return out.trimEnd()
}

const BLOCKS = 'p,li,td,th,h1,h2,h3,h4,h5,h6,pre,blockquote,section,div'

type Position = { node: Text; offset: number }

/**
 * The rendered text under `root`, normalized as `normalizeText` does, with each character's
 * position in the DOM so a match can become a Range.
 */
function indexText(root: HTMLElement): { text: string; positions: Position[] } {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let text = ''
  const positions: Position[] = []
  let node = walker.nextNode() as Text | null
  let block: Element | null = null
  while (node) {
    const value = node.nodeValue ?? ''
    const nodeBlock = node.parentElement?.closest(BLOCKS) ?? null
    // A new block reads as a space, so quotes that span lines or cells still match; inline
    // elements (bold, links) do not split words.
    if (nodeBlock !== block && text !== '' && !text.endsWith(' ')) {
      text += ' '
      positions.push({ node, offset: 0 })
    }
    block = nodeBlock
    for (let offset = 0; offset < value.length; offset++) {
      const char = normalizeChar(value[offset])
      if (char === null || (char === ' ' && (text === '' || text.endsWith(' ')))) continue
      // Lower-casing can turn one character into two; each keeps the source position.
      for (const part of char) {
        text += part
        positions.push({ node, offset })
      }
    }
    node = walker.nextNode() as Text | null
  }
  return { text, positions }
}

export type FindResult = { ranges: Range[]; partial: boolean }

/**
 * Ranges where `query` appears in `root`'s text, ignoring case, whitespace and markdown syntax.
 * A long quote that does not appear whole is looked up by its opening words, flagged `partial`.
 */
export function findText(root: HTMLElement, query: string, { limit = 200 } = {}): FindResult {
  const needle = normalizeText(query)
  if (!needle) return { ranges: [], partial: false }
  const { text, positions } = indexText(root)

  const collect = (target: string) => {
    const ranges: Range[] = []
    let from = text.indexOf(target)
    while (from !== -1 && ranges.length < limit) {
      const start = positions[from]
      const end = positions[from + target.length - 1]
      const range = document.createRange()
      range.setStart(start.node, start.offset)
      range.setEnd(end.node, Math.min(end.offset + 1, end.node.length))
      ranges.push(range)
      from = text.indexOf(target, from + target.length)
    }
    return ranges
  }

  const whole = collect(needle)
  if (whole.length || needle.length < 40) return { ranges: whole, partial: false }
  const opening = needle.split(' ').slice(0, 6).join(' ')
  return { ranges: collect(opening), partial: true }
}

/** Paints ranges with a `::highlight(name)` style; a no-op where the Highlight API is missing. */
export function paintHighlight(name: string, ranges: Range[]) {
  if (typeof CSS === 'undefined' || !('highlights' in CSS)) return
  if (ranges.length) CSS.highlights.set(name, new Highlight(...ranges))
  else CSS.highlights.delete(name)
}
